import {
  Button,
  ButtonProps,
  Color,
  FontWeight,
  forwardRefWithCitrusTheme,
  Text,
  Theme,
} from '@gousto-internal/citrus-react';
import { forwardRef, ReactNode } from 'react';

const StyledMenuCollectionButton = forwardRefWithCitrusTheme(Button, (theme) => [
  baseStyles(theme),
  focusStyles(theme),
  hoverStyles(theme),
  focusAndHoverStyles,
  selectedStyles(theme),
]);

type MenuCollectionButtonProps = Omit<
  ButtonProps,
  'role' | 'children' | 'aria-selected' | 'tabIndex'
> & {
  isSelected: boolean;
  children: ReactNode;
};

export const MenuCollectionButton = forwardRef<HTMLButtonElement, MenuCollectionButtonProps>(
  (props, ref) => {
    const { children, isSelected, ...rest } = props;

    return (
      <StyledMenuCollectionButton
        aria-selected={isSelected}
        role="tab"
        tabIndex={isSelected ? 0 : -1}
        ref={ref}
        {...rest}
      >
        <Text
          color={Color.Inherit}
          fontWeight={isSelected ? FontWeight.Bold : FontWeight.Normal}
          size={4}
        >
          {children}
        </Text>
      </StyledMenuCollectionButton>
    );
  },
);
MenuCollectionButton.displayName = 'MenuCollectionButton';

function baseStyles(theme: Theme) {
  return {
    backgroundColor: theme.color(Color.White),
    border: 'none',
    borderRadius: '4px',
    color: theme.color(Color.ColdGrey_800),
    flexShrink: 0,
    margin: 0,
    outline: 'none',
  };
}

function focusStyles(theme: Theme) {
  return {
    '&:focus': {
      boxShadow: `inset 0 0 0 2px ${theme.color(Color.Secondary_600)}`,
      outline: 'none',
      backgroundColor: theme.color(Color.White),
      color: theme.color(Color.ColdGrey_800),
    },
  };
}

function hoverStyles(theme: Theme) {
  return {
    '&:hover': {
      backgroundColor: theme.color(Color.NeutralGrey_50),
      color: theme.color(Color.ColdGrey_800),
      fontWeight: FontWeight.Bold,
      borderRadius: '0px',
    },
  };
}

const focusAndHoverStyles = {
  ':focus,&:focus:hover': {
    borderRadius: '4px',
  },
};

function selectedStyles(theme: Theme) {
  return {
    '&[aria-selected="true"]': {
      color: theme.color(Color.Primary_600),
      boxShadow: `inset 0 -2px 0 0 ${theme.color(Color.Primary_600)}`,
      borderRadius: '0px',
      fontWeight: FontWeight.Bold,
    },
    '&[aria-selected="true"]:focus': {
      borderRadius: '4px',
      boxShadow: `inset 0 0 0 2px ${theme.color(Color.Secondary_600)}`,
    },
  };
}
